if (!customElements.get('clubrelay-money-pricing')) {
  customElements.define(
    'clubrelay-money-pricing',
    class ClubRelayMoneyPricing extends HTMLElement {
      constructor() {
        super();
        this.unsubscribers = [];
        this.variants = new Map();
        this.onSingleQuantityClick = this.handleSingleQuantityClick.bind(this);
      }

      connectedCallback() {
        const dataNode = this.querySelector('[data-cr-product-prices]');
        if (!dataNode) return;

        try {
          const data = JSON.parse(dataNode.textContent);
          data.variants.forEach((variant) => this.variants.set(String(variant.id), variant));
          this.update(data.selectedVariantId);
        } catch (error) {
          console.error('ClubRelay pricing data could not be parsed.', error);
          return;
        }

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.unsubscribers.push(
            subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
              if (!data?.variant?.id) return;
              this.update(data.variant.id);
              this.refreshSingleQuantityButtons();
            })
          );

          if (PUB_SUB_EVENTS.cartUpdate) {
            this.unsubscribers.push(
              subscribe(PUB_SUB_EVENTS.cartUpdate, ({ productVariantId }) => {
                if (productVariantId) this.setVariantCartState(productVariantId, true);
                this.refreshSingleQuantityButtons();
              })
            );
          }
        }

        document.addEventListener('click', this.onSingleQuantityClick, true);
        this.refreshSingleQuantityButtons();
      }

      disconnectedCallback() {
        this.unsubscribers.forEach((unsubscribe) => {
          if (typeof unsubscribe === 'function') unsubscribe();
        });
        this.unsubscribers = [];
        document.removeEventListener('click', this.onSingleQuantityClick, true);
      }

      getButtonVariantId(button) {
        return button.closest('form')?.querySelector('input[name="id"]')?.value || '';
      }

      openCart(button) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.open(button);
          return;
        }

        window.location.assign(window.routes?.cart_url || '/cart');
      }

      handleSingleQuantityClick(event) {
        if (!(event.target instanceof Element)) return;

        const button = event.target.closest('[data-cr-single-quantity][data-cr-buy-button]');
        if (!button || button.dataset.crInCart !== 'true') return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.openCart(button);
      }

      setVariantCartState(variantId, inCart) {
        document.querySelectorAll('[data-cr-single-quantity][data-cr-buy-button]').forEach((button) => {
          if (String(this.getButtonVariantId(button)) !== String(variantId)) return;

          button.dataset.crInCart = String(inCart);
          const labelNode = button.querySelector('[data-cr-buy-label]');
          const variant = this.variants.get(String(variantId));

          if (inCart) {
            if (labelNode) labelNode.textContent = button.dataset.inCartLabel || 'Already in cart — View cart';
          } else if (labelNode && variant) {
            labelNode.textContent = this.replaceTokens(button.dataset.availableLabel, variant);
          }
        });
      }

      async refreshSingleQuantityButtons() {
        const buttons = document.querySelectorAll('[data-cr-single-quantity][data-cr-buy-button]');
        if (!buttons.length) return;

        try {
          const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
          const response = await fetch(cartUrl, {
            headers: {
              Accept: 'application/json',
            },
          });
          if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

          const cart = await response.json();
          const quantities = new Map(
            cart.items.map((item) => [String(item.variant_id), Number(item.quantity) || 0])
          );

          buttons.forEach((button) => {
            const variantId = this.getButtonVariantId(button);
            this.setVariantCartState(variantId, (quantities.get(String(variantId)) || 0) > 0);
          });
        } catch (error) {
          console.error('ClubRelay cart state could not be loaded.', error);
        }
      }

      replaceTokens(template, variant) {
        return (template || '')
          .replaceAll('[price]', variant.priceFormatted)
          .replaceAll('[compare_at]', variant.compareAtFormatted || variant.priceFormatted)
          .replaceAll('[saving]', variant.savingFormatted || '')
          .replaceAll('[discount]', String(variant.discountPercentage || 0));
      }

      update(variantId) {
        const variant = this.variants.get(String(variantId));
        if (!variant) return;

        document.querySelectorAll('[data-cr-price-current]').forEach((node) => {
          node.textContent = variant.priceFormatted;
        });

        document.querySelectorAll('[data-cr-price-compare]').forEach((node) => {
          node.textContent = variant.compareAtFormatted || '';
          node.hidden = !variant.onSale;
        });

        document.querySelectorAll('[data-cr-price-saving]').forEach((node) => {
          node.textContent = variant.savingFormatted || '';
        });

        document.querySelectorAll('[data-cr-price-discount]').forEach((node) => {
          node.textContent = String(variant.discountPercentage || 0);
        });

        document.querySelectorAll('[data-cr-sale-only]').forEach((node) => {
          node.hidden = !variant.onSale;
        });

        document.querySelectorAll('[data-cr-offer-copy]').forEach((node) => {
          const template = variant.onSale ? node.dataset.saleTemplate : node.dataset.regularTemplate;
          node.textContent = this.replaceTokens(template, variant);
        });

        document.querySelectorAll('input[data-cr-variant-id]').forEach((input) => {
          input.value = variant.id;
          input.disabled = !variant.available;
        });

        document.querySelectorAll('[data-cr-buy-button]').forEach((button) => {
          button.disabled = !variant.available;
          const label = variant.available ? button.dataset.availableLabel : button.dataset.soldOutLabel;
          const labelNode = button.querySelector('[data-cr-buy-label]');
          if (labelNode && label) labelNode.textContent = this.replaceTokens(label, variant);
        });

        document.dispatchEvent(
          new CustomEvent('clubrelay:price-change', {
            detail: variant,
          })
        );
      }
    }
  );
}

if (!window.clubRelayDigitalCartGuard) {
  class ClubRelayDigitalCartGuard {
    constructor() {
      this.quantities = new Map();
      this.unsubscribe = null;
      this.observer = null;
      this.lockFrame = null;
      this.onDocumentClick = this.handleDocumentClick.bind(this);
    }

    init() {
      document.documentElement.dataset.crDigitalCart = 'true';
      document.addEventListener('click', this.onDocumentClick, true);

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ productVariantId }) => {
          if (productVariantId) this.quantities.set(String(productVariantId), 1);
          this.queueQuantityLock();
          this.refreshCart();
        });
      }

      this.observer = new MutationObserver(() => this.queueQuantityLock());
      this.observer.observe(document.body, { childList: true, subtree: true });

      this.refreshCart();
      this.queueQuantityLock();
    }

    getVariantId(button) {
      return button.closest('form')?.querySelector('input[name="id"]')?.value || '';
    }

    openCart(button) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open(button);
        return;
      }

      window.location.assign(window.routes?.cart_url || '/cart');
    }

    handleDocumentClick(event) {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('product-form button[type="submit"][name="add"]');
      if (!button) return;

      const variantId = this.getVariantId(button);
      if (!variantId || (this.quantities.get(String(variantId)) || 0) < 1) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.openCart(button);
    }

    queueQuantityLock() {
      if (this.lockFrame) return;

      this.lockFrame = requestAnimationFrame(() => {
        this.lockFrame = null;
        this.lockQuantityControls();
      });
    }

    lockQuantityControls() {
      document.querySelectorAll('quantity-input .quantity__input').forEach((input) => {
        input.max = '1';
        input.readOnly = true;

        const quantity = input.closest('quantity-input');
        const plusButton = quantity?.querySelector('.quantity__button[name="plus"]');
        if (plusButton) plusButton.disabled = true;
      });
    }

    async refreshCart() {
      try {
        const cartUrl = `${window.routes?.cart_url || '/cart'}.js`;
        const response = await fetch(cartUrl, {
          headers: {
            Accept: 'application/json',
          },
        });
        if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

        const cart = await response.json();
        this.quantities = new Map(
          cart.items.map((item) => [String(item.variant_id), Number(item.quantity) || 0])
        );
        this.queueQuantityLock();
      } catch (error) {
        console.error('ClubRelay digital-cart guard could not load the cart.', error);
      }
    }
  }

  window.clubRelayDigitalCartGuard = new ClubRelayDigitalCartGuard();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.clubRelayDigitalCartGuard.init(), { once: true });
  } else {
    window.clubRelayDigitalCartGuard.init();
  }
}
